import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Loader,
  Building,
  Mail,
  Phone,
} from "lucide-react";
import { api } from "../api/greenclub";

const FinalizeClinicRegistration = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [preCadastro, setPreCadastro] = useState(null);
  const [formData, setFormData] = useState({
    responsavel_nome: "",
    senha: "",
    confirmarSenha: "",
    telefone: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = searchParams.get("token");

    if (!tokenFromUrl) {
      setError("Token de finalização não encontrado. Verifique o link.");
      setLoading(false);
      return;
    }
    setToken(tokenFromUrl);

    const fetchPreCadastro = async () => {
      try {
        const response = await api.getClinicPreRegistrationByToken(
          tokenFromUrl
        );
        if (response.success) {
          setPreCadastro(response.data);
          setFormData((prev) => ({
            ...prev,
            telefone: response.data.telefone || "",
          }));
        } else {
          setError(
            response.error || "Token inválido, expirado ou já utilizado."
          );
        }
      } catch (err) {
        setError(
          "Erro ao buscar dados do pré-cadastro. Tente novamente mais tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPreCadastro();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.responsavel_nome) {
      setError("O nome do responsável é obrigatório.");
      setLoading(false);
      return;
    }

    if (!formData.telefone) {
      setError("O telefone é obrigatório.");
      setLoading(false);
      return;
    }

    if (formData.senha.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      setLoading(false);
      return;
    }

    if (formData.senha !== formData.confirmarSenha) {
      setError("As senhas não coincidem.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.finalizeClinicPreRegistration({
        token,
        senha: formData.senha,
        responsavel_nome: formData.responsavel_nome,
        telefone: formData.telefone,
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || "Não foi possível finalizar o cadastro.");
      }
    } catch (err) {
      setError("Ocorreu um erro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 text-emerald-600 animate-spin" />
        <p className="ml-3 text-gray-700">Verificando convite...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Link Inválido
          </h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => (window.location.href = "/")}
            className="mt-6 w-full bg-emerald-600 text-white py-2 rounded-lg font-medium hover:bg-emerald-700"
          >
            Voltar ao Início
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Cadastro Finalizado!
          </h2>
          <p className="text-gray-600">
            Seu cadastro foi concluído com sucesso. Você já pode acessar o
            portal de clínicas.
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="mt-6 w-full bg-emerald-600 text-white py-2 rounded-lg font-medium hover:bg-emerald-700"
          >
            Ir para o Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Finalize o cadastro da clínica, {preCadastro?.nome}!
        </h1>
        <p className="text-gray-600 mb-6">
          Você foi convidado para se juntar à GreenClub. Complete os dados para
          ativar seu acesso.
        </p>

        <div className="space-y-3 text-sm text-gray-700 mb-6 p-4 bg-gray-50 rounded-lg border">
          <p className="flex items-center">
            <Building className="w-4 h-4 mr-2 text-gray-500" />
            <strong>Clínica:</strong>&nbsp;{preCadastro?.nome}
          </p>
          <p className="flex items-center">
            <Mail className="w-4 h-4 mr-2 text-gray-500" />
            <strong>Email:</strong>&nbsp;{preCadastro?.email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Responsável *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={formData.responsavel_nome}
                onChange={(e) =>
                  setFormData({ ...formData, responsavel_nome: e.target.value })
                }
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                placeholder="Nome completo do responsável"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone de Contato *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="tel"
                value={formData.telefone}
                onChange={(e) =>
                  setFormData({ ...formData, telefone: e.target.value })
                }
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                placeholder="(11) 99999-0000"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Crie sua senha *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? "text" : "password"}
                value={formData.senha}
                onChange={(e) =>
                  setFormData({ ...formData, senha: e.target.value })
                }
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                placeholder="Mínimo 6 caracteres"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirme sua senha *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmarSenha}
                onChange={(e) =>
                  setFormData({ ...formData, confirmarSenha: e.target.value })
                }
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                placeholder="Repita a senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? "Finalizando..." : "Concluir Cadastro e Acessar"}
          </button>
        </form>
      </div>
    </motion.div>
  );
};

export default FinalizeClinicRegistration;
